/**
 * Permission codes the property-management **operations** screens declare an
 * interest in — the front desk, folios, the night audit, channels and
 * commission, ancillary services and board, and guest services.
 *
 * These are the dot-strings `internal/pms/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Nothing here is a
 * security boundary — the server re-checks every route and again in the service
 * layer — but an action a user cannot perform should not be rendered disabled,
 * it should not be rendered at all (§11).
 *
 * P12's `@/modules/pms/permissions` carries the commercial half's codes and
 * says which two of C9's four segregation-of-duties pairs shape a control
 * there. **The other two live here**, and both change what a screen offers:
 *
 *  - **`pms.folio.allowance` ⊗ `pms.night_audit.run`** — the classic hotel
 *    fraud is the person who can write a charge off closing the day past it.
 *    So the folio never offers "write this off and roll the night", the night
 *    audit board carries no allowance affordance, and neither screen infers one
 *    permission from the other. A user who holds both has had a tenant admin
 *    override the conflict with a logged reason; the screens still ask
 *    separately.
 *  - **`pms.folio.city_ledger` ⊗ `pms.folio.reverse`** — transferring a balance
 *    to a company and then reversing the charges behind it removes a receivable
 *    with no trace on the guest's side. The folio therefore renders *Reverse*
 *    and *Transfer to city ledger* as two independently-gated actions and never
 *    as one menu the caller assumes they can reach both halves of.
 *
 * The third pair worth stating is not an SoD rule but the same instinct:
 * **`pms.folio.read` is not implied by `pms.frontdesk.read`.** An arrivals
 * board shows a folio balance because the board endpoint returns one; opening
 * the folio behind it is a separate authority, and the link is only rendered
 * when the caller holds it. C9 §6.14's self-service trap is exactly this pivot
 * — a room id to a folio — approached from the other side.
 *
 * `moduleManage` guards no route. It is the `<module>.manage` fallback
 * `core/rules` consults when no authority band covers an overridable rule's doc
 * type; without it a forced night-audit roll or a late charge returns
 * `ERR_OVERRIDE_NOT_AUTHORISED` on a tenant with no matrix. It is listed so a
 * screen can explain a refusal rather than merely report one.
 */

export const PMS_OPS_PERMISSIONS = {
  /* front desk — C9 §6.1 family 6 */
  frontDeskRead: 'pms.frontdesk.read',
  checkInPerform: 'pms.checkin.perform',
  checkOutPerform: 'pms.checkout.perform',
  roomAssign: 'pms.room.assign',
  roomMove: 'pms.room.move',
  keyIssue: 'pms.key.issue',

  /* folios — §6.1 family 7 */
  folioRead: 'pms.folio.read',
  folioPost: 'pms.folio.post',
  /** The only correction path for a posted charge. SoD partner of `folioCityLedger`. */
  folioReverse: 'pms.folio.reverse',
  folioRoute: 'pms.folio.route',
  folioSettle: 'pms.folio.settle',
  /** MFA-stepped: it writes money off. SoD partner of `nightAuditRun`. */
  folioAllowance: 'pms.folio.allowance',
  /** MFA-stepped: it raises a real AR receivable. SoD partner of `folioReverse`. */
  folioCityLedger: 'pms.folio.city_ledger',
  depositManage: 'pms.deposit.manage',

  /* services and board — §6.1 family 9 */
  serviceRead: 'pms.service.read',
  serviceManage: 'pms.service.manage',
  serviceBook: 'pms.service.book',
  boardManage: 'pms.board.manage',
  transferManage: 'pms.transfer.manage',
  requestManage: 'pms.request.manage',
  /** Authority-checked and cause-coded — never inferred from `requestManage`. */
  requestRecover: 'pms.request.recover',

  /* channels — §6.1 family 10 */
  channelRead: 'pms.channel.read',
  channelManage: 'pms.channel.manage',
  channelExchange: 'pms.channel.exchange',
  commissionManage: 'pms.commission.manage',
  /** SoD partner of `commissionWriteOff`: matching and writing off are two halves. */
  commissionMatch: 'pms.commission.match',
  commissionWriteOff: 'pms.commission.write_off',

  /* night audit — §6.1 family 11 */
  nightAuditRead: 'pms.night_audit.read',
  nightAuditRun: 'pms.night_audit.run',
  /** MFA-stepped: it posts past a control, and opens a queue item. */
  nightAuditForce: 'pms.night_audit.force',
  reportRead: 'pms.report.read',

  /* masters the operations screens read through — §6.1 family 1 */
  propertyRead: 'pms.property.read',
  /** Not a route guard — the authority fallback for an overridable rule. */
  moduleManage: 'pms.module.manage',
} as const

/** Codes owned by other modules that the operations screens read through. */
export const PMS_OPS_EXTERNAL_PERMISSIONS = {
  companyRead: 'org.company.read',
  /** The GL entry the night audit posted, drilled through from the run board. */
  journalRead: 'gl.journal.read',
  /** The AR invoice a city-ledger transfer raises. */
  arInvoiceRead: 'ar.invoice.read',
  bankAccountRead: 'cash.account.read',
  itemRead: 'inventory.item.read',
} as const

export type PmsOpsPermission =
  (typeof PMS_OPS_PERMISSIONS)[keyof typeof PMS_OPS_PERMISSIONS]
